import { api, type MutOpts } from "@/api"
import { queryOptions, useMutation, useQuery } from "@tanstack/react-query"

import { type components } from "@/api/v1"
import { type FetchOptions } from "@/api/types"
import { queryClient } from "@/lib/query-client"

type DictParam = Record<string, unknown>

// ==================== QUERY KEYS ====================
export const patientKeys = {

  // Patients
  all: ["patients"] as const,
  
  list: <TQuery>(query?: TQuery) => [...patientKeys.all, { query }] as const,

  detail: (id: number, query?: DictParam) => [...patientKeys.list(), id,  { query }] as const,
  create: () => [...patientKeys.all, "create"] as const,
  update: () => [...patientKeys.all, "update"] as const,
  setBillingPathway: () => [...patientKeys.all, "billing-pathway"] as const,
  billableDuration: () => [...patientKeys.all, "billable-duration"] as const,


  // Profile 
  navigation: (currentPatientId: number, query?: DictParam) => [...patientKeys.all, currentPatientId, { query }] as const,
  phoneLocation: (id: number) => [...patientKeys.all, "phone-location", id] as const,
  baselines: (id: number) => [...patientKeys.all, "baselines", id] as const,
  baselineAnalysis: (id: number, latestCount: number) =>
    [...patientKeys.all, "baseline-analysis", id, { latestCount }] as const,
  longevityScore: (id: number, query?: DictParam) => [...patientKeys.all, "longevity-score",  id,  { query} ] as const,
  generateInsights: (query?: DictParam) => [...patientKeys.all, "generate-insights", { query }] as const,
  generateScores: (query?: DictParam) => [...patientKeys.all, "health-score", { query }] as const,
  compliantDays: (id: number, timezone?: string) => [...patientKeys.all, "compliant-days", id, { timezone }] as const,
  generateStrokeRiskIndicator: ( path: DictParam, query?: DictParam) => [...patientKeys.all, "stroke-indicator", { path }, { query }] as const,
  chfRisk: (id: number) => [...patientKeys.all, "chf-risk", id] as const,

  alerts: (id: number, query?: DictParam) => [...patientKeys.all, "alerts", id, { query }] as const,
  alertChat: (id: number, query?: DictParam) => [...patientKeys.all, "alert-chat", id, {query}] as const,
  acknowledgeAlert: () => [...patientKeys.all, "acknowledge-alert"] as const,

  fallEvents: (id: number, query?: DictParam) => [...patientKeys.all, "fall-events", id, { query }] as const,

  caretakers: (id: number) => [...patientKeys.all, "caretakers", id] as const,

  consents: (id: number) => [...patientKeys.all, "consents", id] as const,

  consentOverview: (query?: DictParam) =>
    [...patientKeys.all, "consent-overview", { query }] as const,

  quickFilterCounts: (query?: DictParam) =>
    [...patientKeys.all, "quick-filter-counts", { query }] as const,

  sleepSessions: (query?: DictParam) => [...patientKeys.all, "sleep-sessions", { query }] as const,

  pathways: (id: number) => [...patientKeys.all, "pathways", id] as const,
  pathwayAnswers: (pathwayId: number, patientId: number) => [...patientKeys.all, "pathway-answers",  pathwayId, patientId ] as const,

  diary: (patientId: number, query?: DictParam) => [...patientKeys.all, 'diary', patientId, { query }] as const,
  diarySummary: (patientId: number, timezone: string) => [...patientKeys.all, "diary-summary", patientId, timezone] as const,

  sms: (id: number, query?: DictParam) => [...patientKeys.all, "sms", id, {query}] as const,
  sendSms: () => [...patientKeys.all, "send-sms"] as const,

  adtEvents: (id: number, query?: DictParam) => [...patientKeys.all, "adt-events", id, {query}] as const,

  auditLog: (id: number, query?: DictParam) => [...patientKeys.all, "audit-log", id, { query }] as const,

  treatments: (id: number) => [...patientKeys.all, "treatments", id] as const,

  medications: (id: number) => [...patientKeys.all, "medications", id] as const,

  // Provider instructions
  instructions: (id: number, query?: DictParam) =>
    [...patientKeys.all, "instructions", id, { query }] as const,
  // Kept under the same [..., "instructions", id] prefix so invalidateInstructions
  // refreshes reminders too.
  instructionReminders: (id: number, instructionId: number, days: number) =>
    [...patientKeys.all, "instructions", id, "reminders", instructionId, { days }] as const,
  createInstruction: () => [...patientKeys.all, "instructions", "create"] as const,
  updateInstruction: () => [...patientKeys.all, "instructions", "update"] as const,
  discontinueInstruction: () =>
    [...patientKeys.all, "instructions", "discontinue"] as const,
  approveInstruction: () => [...patientKeys.all, "instructions", "approve"] as const,
  draftInstruction: () => [...patientKeys.all, "instructions", "draft"] as const,

  healthSummaries: (id: number, query?: DictParam) => [...patientKeys.all, "health-summaries", id, {query}] as const,
  acknowledgeHealthSummary: () => [...patientKeys.all, "health-summaries", "acknowledge"] as const,

  apneaScreenings: (id: number) => [...patientKeys.all, "apnea-screenings", id] as const,
  acknowledgeApneaScreening: () => [...patientKeys.all, "apnea-screenings", "acknowledge"] as const,
  apneaAssessment: (patientId: number, screeningId: number) => [...patientKeys.all, "apnea-assessment",  patientId, screeningId] as const,
  apneaSessionMetrics: (patientId: number, screeningId: number) => [...patientKeys.all, "apnea-session-metrics", patientId, screeningId] as const,


  // Notes
  note: () => [...patientKeys.all, "note"] as const,
  noteList: <TQuery, TPath>(path: TPath, query?: TQuery) =>
    query === undefined
      ? ([...patientKeys.note(), "list", path] as const)
      : ([...patientKeys.note(), "list", path, query] as const),
  createNote: () => [...patientKeys.note(), "create"] as const,

  // Health Metrics
  heartRate: (query: DictParam)=> [...patientKeys.all, "heart-rate", {query}] as const,
  respiratoryRate: (query: DictParam)=> [...patientKeys.all, "respiratory-rate", {query}] as const,
  spo2: (query: DictParam)=> [...patientKeys.all, "spo2", {query}] as const,
  steps: (query: DictParam)=> [...patientKeys.all, "steps", {query}] as const,
  caloriesBurned: (query: DictParam)=> [...patientKeys.all, "calories-burned", {query}] as const,
  caloriesConsumed: (query: DictParam)=> [...patientKeys.all, "calories-consumed", {query}] as const,
  glucose: (query: DictParam)=> [...patientKeys.all, "glucose", {query}] as const,
  stress: (query: DictParam)=> [...patientKeys.all, "stress", {query}] as const,
  bp: (query: DictParam)=> [...patientKeys.all, "bp", {query}] as const,
  sleep: (query: DictParam)=> [...patientKeys.all, "sleep", {query}] as const,
  weight: (query: DictParam)=> [...patientKeys.all, "weight", {query}] as const,
  temperature: (query: DictParam)=> [...patientKeys.all, "temperature", {query}] as const,
  hrv: (query: DictParam)=> [...patientKeys.all, "hrv", {query}] as const,
  waterIntake: (query: DictParam)=> [...patientKeys.all, "water-intake", {query}] as const,
  inr: (query: DictParam)=> [...patientKeys.all, "inr", {query}] as const,
  nutritionSummary: (query: DictParam)=> [...patientKeys.all, "nutrition-summary", {query}] as const,
  medicationTreatmentSummary: (query: DictParam)=> [...patientKeys.all, "medication-treatment-summary", {query}] as const,

  adtPathways: (patientId: number) => [...patientKeys.all, "adt-pathways", patientId] as const,
  adtPathwayDetail: (id: number) => [...patientKeys.all, "adt-pathway-detail", id] as const,
}

export const patientsQueryOptions = (
  fetchOpts?: FetchOptions<"get", "/patients/">
) => {
  const query = fetchOpts?.params?.query

  return queryOptions({
    queryKey: patientKeys.list({ query }),
    queryFn: () =>
      api.GET("/patients/", { params: { query } }).then(({ data }) => data),
    staleTime: 5 * 60 * 1000, // 5 min
  })
}

/** All six quick-filter badge counts for the patient list in a single request,
 * replacing the per-badge `pageSize=1` list calls. Send the base filters only
 * (facility, clinician, search, isActive, …); the badge toggles are counted
 * independently server-side, so they're not part of the base context here. */
export const quickFilterCountsQueryOptions = (
  fetchOpts?: FetchOptions<"get", "/patients/quick-filter-counts/">
) => {
  const query = fetchOpts?.params?.query

  return queryOptions({
    queryKey: patientKeys.quickFilterCounts({ query }),
    queryFn: () =>
      api
        .GET("/patients/quick-filter-counts/", { params: { query } })
        .then(({ data, error }) => {
          // openapi-fetch resolves (not rejects) on HTTP errors; surface them
          // so the query fails instead of silently resolving undefined.
          if (error) throw error
          return data
        }),
    staleTime: 5 * 60 * 1000, // 5 min
  })
}

/** Lean, paginated per-patient consent overview (identity + consentStatus) for
 * the consent report. Lighter than the full patients list. */
export const consentOverviewQueryOptions = (
  fetchOpts?: FetchOptions<"get", "/patients/consent/overview/">
) => {
  const query = fetchOpts?.params?.query

  return queryOptions({
    queryKey: patientKeys.consentOverview({ query }),
    queryFn: () =>
      api
        .GET("/patients/consent/overview/", { params: { query } })
        .then(({ data, error }) => {
          // openapi-fetch resolves (not rejects) on HTTP errors; surface them
          // so the query fails instead of silently resolving undefined.
          if (error) throw error
          return data
        }),
    staleTime: 5 * 60 * 1000, // 5 min
  })
}

export const usePatientsQuery = (
  fetchOpts?: FetchOptions<"get", "/patients/">
) => {
  const query = fetchOpts?.params?.query

  return useQuery({
    queryKey: patientKeys.list(query),
    queryFn: () => {
      return api
        .GET("/patients/", { params: { query } })
        .then(({ data }) => data)
    },
    staleTime: 5 * 60 * 1000, // 5 min
  })
}

export const usePatientNotes = (
  fetchOpts?: FetchOptions<"get", "/patients/{id}/notes/">,
  options?: { enabled?: boolean }
) => {
  const query = fetchOpts?.params?.query
  const id = fetchOpts?.params?.path?.id as number

  return useQuery({
    queryKey: patientKeys.noteList( { id }, query ),
    queryFn: () => { return api.GET("/patients/{id}/notes/", { params: { query: { pageSize: 5, ...query }, path: { id } },}).then(({ data }) => data)},
    ...options,
    enabled: !!id && (options?.enabled ?? true),
  })
}

export const useHeartRateDataQuery = (
  fetchOpts: FetchOptions<"get", "/patients/heart-rate/">,
  options?: { enabled?: boolean },
) => {
  const query = fetchOpts.params.query
  return useQuery({
    queryKey: patientKeys.heartRate(query),
    queryFn: () => {
      return api
        .GET("/patients/heart-rate/", { params: { query } })
        .then(({ data }) => data as unknown)
    },
    staleTime: 5 * 60 * 1000, // 5 min
    enabled: options?.enabled ?? true,
  })
}

export const useRespiratoryRateDataQuery = (
  fetchOpts: FetchOptions<"get", "/patients/respiratory-rate/">,
  options?: { enabled?: boolean },
) => {
  const query = fetchOpts.params.query

  return useQuery({
    queryKey: patientKeys.respiratoryRate(query),
    queryFn: () => {
      return api
        .GET("/patients/respiratory-rate/", { params: { query } })
        .then(({ data }) => data as unknown)
    },
    staleTime: 5 * 60 * 1000, // 5 min
    enabled: options?.enabled ?? true,
  })
}

export const useSpo2DataQuery = (
  fetchOpts: FetchOptions<"get", "/patients/spo2/">,
  options?: { enabled?: boolean },
) => {
  const query = fetchOpts.params.query

  return useQuery({
    queryKey: patientKeys.spo2(query),
    queryFn: () => {
      return api
        .GET("/patients/spo2/", { params: { query } })
        .then(({ data }) => data as unknown)
    },
    staleTime: 5 * 60 * 1000, // 5 min
    enabled: options?.enabled ?? true,
  })
}

export const useStepsDataQuery = (
  fetchOpts: FetchOptions<"get", "/patients/steps/">,
  options?: { enabled?: boolean },
) => {
  const query = fetchOpts.params.query

  return useQuery({
    queryKey: patientKeys.steps(query),
    queryFn: () => {
      return api
        .GET("/patients/steps/", { params: { query } })
        .then(({ data }) => data as unknown)
    },
    staleTime: 5 * 60 * 1000, // 5 min
    enabled: options?.enabled ?? true,
  })
}

export const useCaloriesConsumedDataQuery = (
  fetchOpts: FetchOptions<"get", "/patients/calories-consumed/">,
  options?: { enabled?: boolean },
) => {
  const query = fetchOpts.params.query

  return useQuery({
    queryKey: patientKeys.caloriesConsumed(query),
    queryFn: () => {
      return api
        .GET("/patients/calories-consumed/", { params: { query } })
        .then(({ data }) => data as unknown)
    },
    staleTime: 5 * 60 * 1000, // 5 min
    enabled: options?.enabled ?? true,
  })
}

export const useCaloriesBurnedDataQuery = (
  fetchOpts: FetchOptions<"get", "/patients/calories-burned/">,
  options?: { enabled?: boolean },
) => {
  const query = fetchOpts.params.query

  return useQuery({
    queryKey: patientKeys.caloriesBurned(query),
    queryFn: () => {
      return api
        .GET("/patients/calories-burned/", { params: { query } })
        .then(({ data }) => data as unknown)
    },
    staleTime: 5 * 60 * 1000, // 5 min
    enabled: options?.enabled ?? true,
  })
}

export const useGlucoseDataQuery = (
  fetchOpts: FetchOptions<"get", "/patients/glucose/">,
  options?: { enabled?: boolean },
) => {
  const query = fetchOpts.params.query
  return useQuery({
    queryKey: patientKeys.glucose(query),
    queryFn: () => {
      return api
        .GET("/patients/glucose/", { params: { query } })
        .then(({ data }) => data as unknown)
    },
    staleTime: 5 * 60 * 1000, // 5 min
    enabled: options?.enabled ?? true,
  })
}

export const useStressDataQuery = (
  fetchOpts: FetchOptions<"get", "/patients/stress/">,
  options?: { enabled?: boolean },
) => {
  const query = fetchOpts.params.query

  return useQuery({
    queryKey: patientKeys.stress(query),
    queryFn: () => {
      return api
        .GET("/patients/stress/", { params: { query } })
        .then(({ data }) => data as unknown)
    },
    staleTime: 5 * 60 * 1000, // 5 min
    enabled: options?.enabled ?? true,
  })
}

export const useBloodPressureDataQuery = (
  fetchOpts: FetchOptions<"get", "/patients/blood-pressure/">,
  options?: { enabled?: boolean },
) => {
  const query = fetchOpts.params.query
  return useQuery({
    queryKey: patientKeys.bp(query),
    queryFn: () => {
      return api
        .GET("/patients/blood-pressure/", { params: { query } })
        .then(({ data }) => data as unknown)
    },
    staleTime: 5 * 60 * 1000, // 5 min
    enabled: options?.enabled ?? true,
  })
}

export const useHeartRateVariabilityDataQuery = (
  fetchOpts: FetchOptions<"get", "/patients/heart-rate-variability/">,
  options?: { enabled?: boolean },
) => {
  const query = fetchOpts.params.query
  return useQuery({
    queryKey: patientKeys.hrv(query),
    queryFn: () => {
      return api
        .GET("/patients/heart-rate-variability/", { params: { query } })
        .then(({ data }) => data as unknown)
    },
    staleTime: 5 * 60 * 1000, // 5 min
    enabled: options?.enabled ?? true,
  })
}

export const useWeightDataQuery = (
  fetchOpts: FetchOptions<"get", "/patients/weight/">,
  options?: { enabled?: boolean },
) => {
  const query = fetchOpts?.params?.query
  return useQuery({
    queryKey: patientKeys.weight(query),
    queryFn: () => {
      return api
        .GET("/patients/weight/", { params: { query } })
        .then(({ data }) => data as unknown)
    },
    staleTime: 5 * 60 * 1000, // 5 min
    enabled: options?.enabled ?? true,
  })
}

export const useTemperatureDataQuery = (
  fetchOpts: FetchOptions<"get", "/patients/temperature/">,
  options?: { enabled?: boolean },
) => {
  const query = fetchOpts?.params?.query
  return useQuery({
    queryKey: patientKeys.temperature(query),
    queryFn: () => {
      return api
        .GET("/patients/temperature/", { params: { query } })
        .then(({ data }) => data as unknown)
    },
    staleTime: 5 * 60 * 1000, // 5 min
    enabled: options?.enabled ?? true,
  })
}

export const useNutritionSummaryQuery = (
  fetchOpts: FetchOptions<"get", "/patients/nutrition-summary/">,
  options?: { enabled?: boolean },
) => {
  const query = fetchOpts?.params?.query
  return useQuery({
    queryKey: patientKeys.nutritionSummary(query),
    queryFn: () => {
      return api
        .GET("/patients/nutrition-summary/", { params: { query } })
        .then(({ data }) => data as unknown)
    },
    staleTime: 5 * 60 * 1000, // 5 min
    enabled: options?.enabled ?? true,
  })
}

export const useWaterIntakeDataQuery = (
  fetchOpts: FetchOptions<"get", "/patients/water-intake/">,
  options?: { enabled?: boolean },
) => {
  const query = fetchOpts.params.query
  return useQuery({
    queryKey: patientKeys.waterIntake(query),
    queryFn: () => {
      return api
        .GET("/patients/water-intake/", { params: { query } })
        .then(({ data }) => data as unknown)
    },
    staleTime: 5 * 60 * 1000, // 5 min
    enabled: options?.enabled ?? true,
  })
}

export const useInrDataQuery = (
  fetchOpts: FetchOptions<"get", "/patients/inr/">,
  options?: { enabled?: boolean },
) => {
  const query = fetchOpts.params.query
  return useQuery({
    queryKey: patientKeys.inr(query),
    queryFn: () => {
      return api
        .GET("/patients/inr/", { params: { query } })
        .then(({ data }) => data as unknown)
    },
    staleTime: 5 * 60 * 1000, // 5 min
    enabled: options?.enabled ?? true,
  })
}

export const useMedicationTreatmentSummaryQuery = (
  fetchOpts: FetchOptions<"get", "/patients/medication-treatment-summary/">,
  options?: { enabled?: boolean },
) => {
  const query = fetchOpts.params.query
  return useQuery({
    queryKey: patientKeys.medicationTreatmentSummary(query),
    queryFn: () => {
      return api
        .GET("/patients/medication-treatment-summary/", { params: { query } })
        .then(({ data }) => data as unknown)
    },
    staleTime: 5 * 60 * 1000, // 5 min
    enabled: options?.enabled ?? true,
  })
}

export const useSleepDataQuery = (
  fetchOpts: FetchOptions<"get", "/patients/sleep/">,
  options?: { enabled?: boolean },
) => {
  const query = fetchOpts?.params?.query
  return useQuery({
    queryKey: patientKeys.sleep(query),
    queryFn: () => {
      return api
        .GET("/patients/sleep/", { params: { query } })
        .then(({ data }) => data as unknown)
    },
    staleTime: 5 * 60 * 1000, // 5 min
    enabled: options?.enabled ?? true,
  })
}

export const useSleepSessionsDataQuery = (
  fetchOpts: FetchOptions<"get", "/patients/sleep-sessions/">,
  options?: { enabled?: boolean },
) => {
  const query = fetchOpts?.params?.query
  return useQuery({
    queryKey: patientKeys.sleepSessions(query),
    queryFn: () => {
      return api
        .GET("/patients/sleep-sessions/", { params: { query } })
        .then(({ data }) => data as unknown)
    },
    staleTime: 5 * 60 * 1000, // 5 min
    enabled: options?.enabled ?? true,
  })
}

export const patientDetailsQueryOptions = (
  fetchOpts: FetchOptions<"get", "/patients/{id}/">
) => {
  const patientId = fetchOpts.params.path.id
  const query = fetchOpts.params.query

  return queryOptions({
    queryKey: patientKeys.detail(patientId, query ),
    queryFn: () => { return api.GET("/patients/{id}/", {params: {
            path: { id: Number(patientId) },
            query
          }
        })
        .then(({ data }) => data)
    },
    staleTime: 5 * 60 * 1000, // 5 min
  })
}

export const patientInsightsQueryOptions = (
  fetchOpts: FetchOptions<"get", "/patients/ai-report/{id}/">
) => {
  const params = fetchOpts?.params

  return queryOptions({
    queryKey: patientKeys.generateInsights(params?.query),
    queryFn: () => {
      return api
        .GET("/patients/ai-report/{id}/", { params })
        .then(({ data }) => data)
    },
    enabled: false,
  })
}

export type PatientHealthScoreMetricStatus = "in_range" | "borderline" | "out_of_range"

export type PatientHealthScoreMetricKey =
  | "heartRate"
  | "bloodPressure"
  | "spO2"
  | "heartRateVariability"
  | "stress"
  | "respiratoryRate"
  | "bloodGlucose"
  | "inr"
  | "temperature"

export type PatientHealthScoreResult = {
  healthScore?: number
  alertLevel?: PatientHealthScoreMetricStatus
  metrics?: Partial<Record<PatientHealthScoreMetricKey, PatientHealthScoreMetricStatus>>
}

export const usePatientHealthScoreQuery = (
  patientId: number,
  queryParams: { startDate: string; endDate: string; timezone: string },
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: patientKeys.generateScores({ patientId, ...queryParams }),
    queryFn: () => {
      return api
        .GET("/patients/health-score/{id}/", {
          params: {
            path: { id: patientId },
            // @ts-expect-error API schema doesn't declare timezone/startDate/endDate as query params, but the endpoint requires them
            query: queryParams,
          },
        })
        .then(({ data }) => data?.results as PatientHealthScoreResult | undefined)
    },
    enabled: (options?.enabled ?? true) && !!patientId,
    staleTime: 5 * 60 * 1000, // 5 min
  })
}

export const usePatientStrokeRiskIndicatorQuery = (
  fetchOpts: FetchOptions<'get', '/patients/stroke-indicator/{id}/'>,
) => {
  const params = fetchOpts?.params;

  return useQuery({
    queryKey: patientKeys.generateStrokeRiskIndicator(params.path, params?.query),
    queryFn: () => {
      return api.GET('/patients/stroke-indicator/{id}/', { params }).then(({ data }) => data);
    },
    enabled: true
  });
};

export const useCreatePatientMutation = (
  mutationOptions?: MutOpts<"post", "/patients/">
) => {
  return useMutation({
    mutationKey: patientKeys.create(),
    mutationFn: fetchOpts => {
      return api.POST("/patients/", fetchOpts)
    },
    ...mutationOptions,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        queryKey: patientKeys.list(),
      })
      return mutationOptions?.onSuccess?.(...args)
    },
  })
}

export const useUpdatePatientMutation = (
  mutationOptions?: MutOpts<"patch", "/patients/{id}/">
) => {
  return useMutation({
    mutationKey: patientKeys.update(),
    mutationFn: fetchOpts => {
      return api.PATCH("/patients/{id}/", fetchOpts)
    },
    ...mutationOptions,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: patientKeys.list(),
        exact: false,
      })
      // Refresh this patient's detail so fields like `deactivation` and
      // `isActive` reflect the edit. Match by id (the detail key nests the id
      // among object segments, so a prefix key is brittle) and use
      // refetchType:"all" so it refreshes even when the profile tab isn't the
      // actively-observed query.
      const id = variables.params?.path?.id
      if (id != null) {
        queryClient.invalidateQueries({
          predicate: query =>
            query.queryKey[0] === "patients" && query.queryKey.includes(id),
          refetchType: "all",
        })
      }
      return mutationOptions?.onSuccess?.(data, variables, context)
    },
  })
}

export const useSetBillingPathwayMutation = (
  mutationOptions?: MutOpts<"patch", "/patients/{id}/billing-pathway/">
) => {
  return useMutation({
    mutationKey: patientKeys.setBillingPathway(),
    mutationFn: fetchOpts => {
      return api.PATCH("/patients/{id}/billing-pathway/", fetchOpts)
    },
    ...mutationOptions,
    onSuccess: (data, variables, context) => {
      // Refresh this patient's detail so `billingPathway` and the subscription
      // fields reflect the switch. Switching to `insurance_billed` cancels the
      // Stripe subscription asynchronously, so the detail card polls these
      // fields until `subscriptionStatus` reads "canceled". Match by id (same
      // predicate as useUpdatePatientMutation) and refetch even when the
      // profile tab isn't the actively-observed query.
      const id = variables.params?.path?.id
      if (id != null) {
        queryClient.invalidateQueries({
          predicate: query =>
            query.queryKey[0] === "patients" && query.queryKey.includes(id),
          refetchType: "all",
        })
      }
      queryClient.invalidateQueries({
        queryKey: patientKeys.list(),
        exact: false,
      })
      return mutationOptions?.onSuccess?.(data, variables, context)
    },
  })
}

export const useCreatePatientNoteMutation = (
  mutationOptions?: MutOpts<"post", "/patients/{id}/notes/">
) => {
  return useMutation({
    mutationKey: patientKeys.createNote(),
    mutationFn: fetchOpts => {
      return api.POST("/patients/{id}/notes/", fetchOpts)
    },
    ...mutationOptions,
    onSuccess: (data, variables, context) => {
      const id = variables.params?.path?.id
      queryClient.invalidateQueries({
        queryKey: patientKeys.noteList({ id }),
      })
      return mutationOptions?.onSuccess?.(data, variables, context)
    },
  })
}

export const useCreatePatientBillableTimeMutation = (
  mutationOptions?: MutOpts<"post", "/patients/billable-duration/">
) => {
  return useMutation({
    mutationKey: patientKeys.billableDuration(),
    mutationFn: fetchOpts => {
      return api.POST("/patients/billable-duration/", fetchOpts)
    },
    ...mutationOptions,
  })
}

export const usePatientDiaryQuery = (
  patientId: number,
  page: number = 1,
  pageSize: number = 10
) => {
  return useQuery({
    queryKey: patientKeys.diary(patientId, { page, pageSize }),
    queryFn: () => {
      return api
        .GET("/patients/diary/", {
          params: {
            query: {
              patientId: Number(patientId),
              page,
              pageSize,
            },
          },
        })
        .then(({ data }) => data);
    },
    staleTime: 5 * 60 * 1000, // 5 min
  });
};

export const usePatientDiarySummaryQuery = (patientId: number, timezone: string) => {
  return useQuery({
    queryKey: patientKeys.diarySummary(patientId, timezone),
    queryFn: () => {
      return api
        .GET("/patients/diary-summary/", {
          params: { query: { patientId: Number(patientId), timezone } },
        })
        .then(({ data }) => data)
    },
    staleTime: 5 * 60 * 1000, // 5 min
    enabled: !!patientId,
  })
}

export const usePatientInsightsQuery = (
  fetchOpts: FetchOptions<"get", "/patients/ai-report/{id}/">
) => useQuery(patientInsightsQueryOptions(fetchOpts))

export const useChfRiskQuery = (
  fetchOpts: FetchOptions<"get", "/patients/chf-risk/{id}/">,
  options?: { enabled?: boolean }
) => {
  const id = fetchOpts.params.path.id
  return useQuery({
    queryKey: patientKeys.chfRisk(id),
    queryFn: async () => {
      const { data, error } = await api.GET("/patients/chf-risk/{id}/", fetchOpts)
      if (error) throw error
      return data
    },
    enabled: (options?.enabled ?? true) && !!id,
  })
}

export const usePatientBaselinesQuery = (
  patientId: number,
  page: number = 1,
  pageSize: number = 3
) => {
  return useQuery({
    queryKey: patientKeys.baselines(patientId),
    queryFn: () => {
      return api
        .GET("/patients/{id}/baselines/", {
          params: {
            path: { id: Number(patientId) },
            query: { page, pageSize }
          }
        })
        .then(({ data }) => data)
    },
    staleTime: 10 * 60 * 1000, // 10 min
  })
}

export const usePatientBaselineAnalysisQuery = (patientId: number, latestCount = 6) => {
  return useQuery({
    queryKey: patientKeys.baselineAnalysis(patientId, latestCount),
    queryFn: async () => {
      const { data, error } = await api.GET("/patients/{id}/baseline-overview/", {
        params: {
          path: { id: Number(patientId) },
          query: { latestCount },
        },
      })

      if (error) throw error

      return data
    },
    staleTime: 10 * 60 * 1000, // 10 min
    enabled: !!patientId,
  })
}


export const useHealthFormOptionsQuery = () => {
  return useQuery({
    queryKey: ["health-form-options"],
    queryFn: () => { return api.GET("/patients/health-form-options/").then(({ data }) => data) }
  })
}

// The generated schema for this endpoint is stale: it describes a single
// GetPatientAlerts object, but the backend actually paginates and returns
// { message, results: GetPatientAlerts[], pagination }.
export type PaginatedPatientAlerts = {
  message: string
  results: components["schemas"]["GetPatientAlerts"][]
  pagination: {
    isFirstPage: boolean
    isLastPage: boolean
    currentPage: number
    previousPage: number
    nextPage: number
    pageCount: number
    totalCount: number
  }
}

export const usePatientAlertsQuery = (fetchOpts: {
  params: {
    path: { id: number }
    query?: {
      startDate?: string
      endDate?: string
      page?: number
      pageSize?: number
    }
  }
}) => {
  const query = fetchOpts?.params?.query
  const id = fetchOpts?.params.path.id

  return useQuery({
    queryKey: patientKeys.alerts(id, query),
    queryFn: () => {
      return api
        .GET("/patients/{id}/alerts/", {
          params: { query, path: { id } },
        })
        .then(({ data }) => data as unknown as PaginatedPatientAlerts | undefined)
    },
    staleTime: 0, // always fresh
  })
}


export const usePatientFallEventsQuery = (
  fetchOpts: FetchOptions<"get", "/patients/{id}/fall-events/">,
  options?: { enabled?: boolean }
) => {
  const query = fetchOpts?.params?.query
  const id = fetchOpts?.params.path.id
  return useQuery({
    queryKey: patientKeys.fallEvents(id, query),
    queryFn: () => {
      return api
        .GET("/patients/{id}/fall-events/", {
          params: { query, path: { id } },
        })
        .then(({ data }) => data)
    },
    enabled: (options?.enabled ?? true) && !!id,
    staleTime: 0, // always fresh
  })
}

export const usePatientCaretakersQuery = (
  fetchOpts: FetchOptions<"get", "/patients/{id}/caretakers/">,
  options?: { enabled?: boolean }
) => {
  const id = fetchOpts?.params.path.id
  return useQuery({
    queryKey: patientKeys.caretakers(id),
    queryFn: () => {
      return api
        .GET("/patients/{id}/caretakers/", { params: { path: { id } } })
        .then(({ data }) => data)
    },
    enabled: (options?.enabled ?? true) && !!id,
  })
}

// The generated schema types the audit-log response as a single entry and omits
// page/pageSize, so we declare the real envelope + query here.
export type PatientAuditLogRow = {
  id: number
  action: "create" | "update" | "delete" | "access"
  timestamp: string
  actor: { id: number; fullName: string; email: string } | null
  remoteAddr: string | null
  objectRepr: string
  changes: { field: string; old: unknown; new: unknown }[]
}

export type PatientAuditLogResponse = {
  message: string
  results: PatientAuditLogRow[]
  pagination: components["schemas"]["DiaryPagination"]
}

export type PatientAuditLogQueryParams = {
  action?: string
  startDate?: string
  endDate?: string
  timezone?: string
  search?: string
  page?: number
  pageSize?: number
}

export const usePatientAuditLogQuery = (
  patientId: number,
  query: PatientAuditLogQueryParams
) => {
  return useQuery({
    queryKey: patientKeys.auditLog(patientId, query),
    queryFn: () =>
      api
        .GET("/patients/audit-log/", {
          // page/pageSize aren't in the generated query type but the endpoint
          // accepts them; they're serialized at runtime regardless.
          params: { query: { patientId, ...query } as { patientId: number } },
        })
        .then(({ data }) => data as unknown as PatientAuditLogResponse),
    enabled: !!patientId,
    staleTime: 0,
  })
}

export const usePatientHealthSummariesQuery = (
  fetchOpts: FetchOptions<"get", "/patients/{id}/health-summaries/">
) => {
  const query = fetchOpts?.params?.query
  const id = fetchOpts?.params.path.id
  return useQuery({
    queryKey: patientKeys.healthSummaries(id, query),
    queryFn: () => {
      return api
        .GET("/patients/{id}/health-summaries/", {
          params: { query, path: { id } },
        })
        .then(({ data }) => data)
    },
    enabled: !!id,
    staleTime: 10 * 60 * 1000, // 10 min
  })
}

export const useAcknowledgeHealthSummaryMutation = (
  mutationOptions?: MutOpts<"patch", "/patients/{id}/health-summaries/{summary_id}/acknowledge/">
) => {
  return useMutation({
    mutationKey: patientKeys.acknowledgeHealthSummary(),
    mutationFn: (fetchOpts) => {
      return api.PATCH("/patients/{id}/health-summaries/{summary_id}/acknowledge/", fetchOpts)
    },
    ...mutationOptions,
    onSuccess: (...args) => {
      const patientId = args[1]?.params?.path?.id
      if (patientId) {
        queryClient.invalidateQueries({
          queryKey: [...patientKeys.all, "health-summaries", patientId],
        })
      }
      return mutationOptions?.onSuccess?.(...args)
    },
  })
}



export const usePatientNavigationQuery = (
  fetchOpts: {
    params: {
      currentPatientId: number,
      query?: Record<string, unknown>
    }
  }
) => {
  const { currentPatientId, query } = fetchOpts.params

  return useQuery({
    queryKey: patientKeys.navigation(currentPatientId, query ?? {}),
    queryFn: () => {
      return api
        .GET("/patients/navigation/", {
          params: {
            query: {
              currentPatientId: String(currentPatientId),
              ...query
            }
          }
        })
        .then(({ data }) => data)
    },
    staleTime: 5 * 60 * 1000, // 5 min
  })
}


export const usePatientPhoneLocationQuery = (patientId: number) => {
  return useQuery({
    queryKey: patientKeys.phoneLocation(patientId),
    queryFn: () => {
      return api
        // @ts-expect-error API
        .GET(`/patients/${patientId}/phone-location/`)
        .then(({ data }) => data)
    },
  })
}

export const usePatientLongevityScoreQuery = (
  fetchOpts: FetchOptions<"get", "/patients/{id}/longevity-score/">
) => {
  const query = fetchOpts?.params?.query
  const id = fetchOpts?.params.path.id

  return useQuery({
    queryKey: patientKeys.longevityScore(id, query),
    queryFn: () => {
      return api
        .GET("/patients/{id}/longevity-score/", {
          params: { query, path: { id } },
        })
        .then(({ data }) => data)
    },
    staleTime: 5 * 60 * 1000, // 5 min
  })
}

export const usePatientCompliantDaysQuery = (
  patientId: number
) => {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone

  return useQuery({
    queryKey: patientKeys.compliantDays(patientId, timezone),
    queryFn: () =>
      api.GET("/patients/{id}/compliant-days/", {
        params: {
          path: { id: patientId },
          query: { timezone },
        },
      }).then(({ data }) => data),
  })
}

export const usePatientPathwaysQuery = (
  patientId: number
) => {
  return useQuery({
    queryKey: patientKeys.pathways(patientId),
    queryFn: () => {return api.GET("/patients/{id}/sequences/", {
          params: { path: { id: patientId } },
        })
      .then(({ data }) => data)
    },
    staleTime: 10 * 60 * 1000, // 10 min
  })
}

export const usePatientPathwayAnswersQuery = (
  pathwayId: number,
  patientId: number
) => {
  return useQuery({
    queryKey: patientKeys.pathwayAnswers(pathwayId, patientId),
    queryFn: () => {
      return api
        .GET("/pathways/{pathway_id}/patient/{id}", {
          params: { path: { pathwayId, id: patientId } },
        })
        .then(({ data }) => data)
    },
    enabled: !!pathwayId && !!patientId,
    staleTime: 10 * 60 * 1000, // 10 min
  })
}

export const usePatientAlertChatQuery = (
  patientId: number,
  fetchOpts?: { params?: { query?: { page?: number; pageSize?: number } } }
) => {
  const page = fetchOpts?.params?.query?.page
  const pageSize = fetchOpts?.params?.query?.pageSize

  return useQuery({
    queryKey: patientKeys.alertChat(patientId, fetchOpts?.params?.query),
    queryFn: () => {
      return api
        .GET("/chats/patient/{id}/alerts/", {
          params: { query: { page, pageSize }, path: { id: patientId } },
        })
        .then(({ data }) => data)
    },
    enabled: !!patientId,
    staleTime: 0, // always fresh
  })
}

export const useAcknowledgePatientAlertMutation = (
  mutationOptions?: MutOpts<"patch", "/chats/alerts/{id}/">
) => {
  return useMutation({
    mutationKey: patientKeys.acknowledgeAlert(),
    mutationFn: fetchOpts => {
      return api.PATCH("/chats/alerts/{id}/", fetchOpts)
    },
    ...mutationOptions,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({queryKey: [...patientKeys.all, "alert-chat"], exact: false})
      return mutationOptions?.onSuccess?.(data, variables, context)
    },
  })
}

export const usePatientSmsQuery = (
  patientId: number,
  fetchOpts?: { params?: { query?: DictParam } }
) => {
  const query = fetchOpts?.params?.query

  return useQuery({
    queryKey: patientKeys.sms(patientId, query),
    queryFn: () => {
      return api
        .GET("/patients/{id}/sms/", {
          params: { query, path: { id: patientId } },
        })
        .then(({ data }) => data)
    },
    enabled: !!patientId,
    staleTime: 0, // always fresh
  })
}

export const usePatientSmsMutation = (
  mutationOptions?: MutOpts<"post", "/patients/{id}/sms/">
) => {
  return useMutation({
    mutationKey: patientKeys.sendSms(),
    mutationFn: fetchOpts => {
      return api.POST("/patients/{id}/sms/", fetchOpts)
    },
    ...mutationOptions,
    onSuccess: (data, variables, context) => {
      const id = variables.params.path.id
      queryClient.invalidateQueries({queryKey: patientKeys.sms(id)})
      return mutationOptions?.onSuccess?.(data, variables, context)
    },
  })
}

/** The full consent catalog for a patient (current v3, legacy v2/v1), newest
 * version first. Unsigned entries come back with status `not_signed` and no
 * `downloadType`; signed ones carry the `downloadType` for the signed-copy
 * download endpoint. The endpoint wraps the list in a `{ consents }` envelope. */
export const usePatientConsentsQuery = (patientId?: number) => {
  return useQuery({
    queryKey: patientKeys.consents(patientId as number),
    queryFn: () =>
      api
        .GET("/patients/{id}/consents/", {
          params: { path: { id: patientId as number } },
        })
        .then(({ data, error }) => {
          // openapi-fetch resolves (not rejects) on HTTP errors, so surface
          // them to React Query — otherwise a 4xx/5xx looks like an empty list.
          if (error) throw error
          return data?.consents ?? []
        }),
    enabled: !!patientId,
  })
}

export const useApneaScreeningsQuery = (patientId: number) => {
  return useQuery({
    queryKey: patientKeys.apneaScreenings(patientId),
    queryFn: () =>
      api
        .GET("/patients/{id}/apnea-screenings/", {
          params: { path: { id: patientId } },
        })
        .then(({ data }) => data),
    enabled: !!patientId,
    staleTime: 10 * 60 * 1000, // 10 min
  })
}

export const useApneaAssessmentQuery = (
  patientId: number,
  screeningId: number,
  enabled = true
) => {
  return useQuery({
    queryKey: patientKeys.apneaAssessment(patientId, screeningId),
    queryFn: () =>
      api.GET("/patients/{id}/apnea-screenings/{screening_id}/assessment/", {
        params: {
          path: {
            id: patientId,
            // @ts-expect-error API
            screening_id: screeningId,
          },
        },
      })
        .then(({ data }) => data),
    enabled: !!patientId && !!screeningId && enabled,
    staleTime: 10 * 60 * 1000, // 10 min
  })
}


export const useApneaSessionMetricsQuery = (
  patientId: number,
  screeningId: number,
  enabled = true
) => {
  return useQuery({
    queryKey: patientKeys.apneaSessionMetrics(patientId, screeningId),
    queryFn: () => {
      return api
        .GET("/patients/{id}/apnea-screenings/{screening_id}/session-metrics/", {
          params: {
            path: {
              id: patientId,
              // @ts-expect-error API
              screening_id: screeningId,
            },
          },
        })
        .then(({ data }) => data)
    },
    enabled: !!patientId && !!screeningId && enabled,
    staleTime: 10 * 60 * 1000, // 10 min
  })
}



// TODO(api): `isActive` is added by the backend's feat/medication-list-is-active
// branch — served at runtime but not yet on dev's generated PatientMedicationCard.
// Drop this augmentation + cast once that lands on dev and `bun api:sync` picks
// it up.
export type PatientMedicationCard =
  components["schemas"]["PatientMedicationCard"] & { isActive?: boolean }

export const usePatientMedicationsQuery = (patientId: number) => {
  return useQuery({
    queryKey: patientKeys.medications(patientId),
    queryFn: () =>
      api
        .GET("/patients/{id}/medications/", {
          params: { path: { id: patientId } },
        })
        .then(({ data }) => data as PatientMedicationCard[] | undefined),
    enabled: !!patientId,
    staleTime: 10 * 60 * 1000, // 10 min
  })
}

export const usePatientTreatmentsQuery = (patientId: number) => {
  return useQuery({
    queryKey: patientKeys.treatments(patientId),
    queryFn: () =>
      api
        .GET("/patients/{id}/treatments/", {
          params: { path: { id: patientId } },
        })
        .then(({ data }) => data),
    enabled: !!patientId,
    staleTime: 10 * 60 * 1000, // 10 min
  })
}

export const useAnswerApneaAssessmentQuestionMutation = (
  mutationOptions?: MutOpts<
    "post",
    "/patients/{id}/apnea-screenings/{screening_id}/assessment/answer/"
  >
) => {
  return useMutation({
    mutationKey: ["patients", "apnea-assessment", "answer"],
    mutationFn: (fetchOpts: FetchOptions<
      "post",
      "/patients/{id}/apnea-screenings/{screening_id}/assessment/answer/"
    >) => {
      return api.POST(
        "/patients/{id}/apnea-screenings/{screening_id}/assessment/answer/",
        fetchOpts
      )
    },
    ...mutationOptions,
    onSuccess: (...args) => {
      const patientId = args[1]?.params?.path?.id
      // @ts-expect-error API
      const screeningId = args[1]?.params?.path?.screening_id as number
      if (patientId != null && screeningId != null) {
        queryClient.invalidateQueries({
          queryKey: patientKeys.apneaAssessment(patientId, screeningId),
        })
      }
      return mutationOptions?.onSuccess?.(...args)
    },
  })
}

export const usePatientAdtEventsQuery = (
  patientId: number,
  query?: { page?: number; pageSize?: number }
) => {
  return useQuery({
    queryKey: [...patientKeys.adtEvents(patientId), query],
    queryFn: () =>
      api
        .GET("/adt/events/", {
          params: { query: { patient: patientId, ...query } },
        })
        .then(({ data }) => data),
    staleTime: 0, // always fresh
  })
}

export const usePatientAdtPathwaysQuery = (patientId: number) => {
  return useQuery({
    queryKey: patientKeys.adtPathways(patientId),
    queryFn: () =>
      api
        .GET("/adt/patient-pathways/", {
          params: { query: { patient: patientId } },
        })
        .then(({ data }) => data),
    staleTime: 30_000,
  })
}

export const useAdtPathwayDetailQuery = (id: number, enabled = true) => {
  return useQuery({
    queryKey: patientKeys.adtPathwayDetail(id),
    queryFn: () =>
      api
        .GET("/adt/patient-pathways/{id}/", {
          params: { path: { id } },
        })
        .then(({ data }) => data),
    enabled,
  })
}

export const useCreateAdtEventMutation = (
  mutationOptions?: MutOpts<"post", "/adt/events/">
) => {
  return useMutation({
    mutationFn: (fetchOpts: FetchOptions<"post", "/adt/events/">) =>
      api.POST("/adt/events/", fetchOpts),
    ...mutationOptions,
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({ queryKey: patientKeys.all })
      return mutationOptions?.onSuccess?.(...args)
    },
  })
}

// ==================== PROVIDER INSTRUCTIONS ====================

/** Invalidate every cached instruction list for a patient (any status/category
 * filter) after a write. */
const invalidateInstructions = (patientId?: number) => {
  if (patientId == null) return
  queryClient.invalidateQueries({
    queryKey: [...patientKeys.all, "instructions", patientId],
    exact: false,
  })
}

export const useProviderInstructionsQuery = (
  patientId: number,
  query?: {
    status?: ("pending_approval" | "active" | "completed" | "discontinued")[]
    category?: components["schemas"]["PatientProviderInstructionCategoryEnum"][]
  },
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: patientKeys.instructions(patientId, query),
    queryFn: () =>
      api
        .GET("/patients/{id}/instructions/", {
          params: { path: { id: patientId }, query },
        })
        // The list comes back paginated as { message, results, pagination };
        // the card only needs the rows.
        .then(({ data }) => data?.results ?? []),
    enabled: (options?.enabled ?? true) && !!patientId,
    staleTime: 60 * 1000, // 1 min
  })
}

export const useCreateProviderInstructionMutation = (
  mutationOptions?: MutOpts<"post", "/patients/{id}/instructions/">
) => {
  return useMutation({
    mutationKey: patientKeys.createInstruction(),
    mutationFn: (fetchOpts) => api.POST("/patients/{id}/instructions/", fetchOpts),
    ...mutationOptions,
    onSuccess: (...args) => {
      invalidateInstructions(args[1]?.params?.path?.id)
      return mutationOptions?.onSuccess?.(...args)
    },
  })
}

// The generated schema names BOTH path params in
// /patients/{id}/instructions/{id}/ as `id`, so the typed client can't fill the
// patient and instruction ids independently. These instruction-scoped writes
// interpolate the URL themselves (same escape hatch as usePatientPhoneLocation)
// and cast the result back to the instruction type.
type ProviderInstruction = components["schemas"]["PatientProviderInstruction"]

export const useUpdateProviderInstructionMutation = (options?: {
  onSuccess?: (data: ProviderInstruction | undefined) => void
  onError?: (err: unknown) => void
}) => {
  return useMutation({
    mutationKey: patientKeys.updateInstruction(),
    mutationFn: async (vars: {
      patientId: number
      instructionId: number
      body: components["schemas"]["PatchedPatientProviderInstruction"]
    }) => {
      const res = await api
        // @ts-expect-error schema declares both path params as `id`; interpolate directly
        .PATCH(`/patients/${vars.patientId}/instructions/${vars.instructionId}/`, {
          body: vars.body,
        })
      return res.data as ProviderInstruction | undefined
    },
    onSuccess: (data, vars) => {
      invalidateInstructions(vars.patientId)
      options?.onSuccess?.(data)
    },
    onError: options?.onError,
  })
}

export const useDiscontinueProviderInstructionMutation = (options?: {
  onSuccess?: (data: ProviderInstruction | undefined) => void
  onError?: (err: unknown) => void
}) => {
  return useMutation({
    mutationKey: patientKeys.discontinueInstruction(),
    mutationFn: async (vars: {
      patientId: number
      instructionId: number
      reason: string
    }) => {
      const res = await api
        // @ts-expect-error schema declares both path params as `id`; interpolate directly
        .POST(`/patients/${vars.patientId}/instructions/${vars.instructionId}/discontinue/`, {
          body: { reason: vars.reason },
        })
      return res.data as ProviderInstruction | undefined
    },
    onSuccess: (data, vars) => {
      invalidateInstructions(vars.patientId)
      options?.onSuccess?.(data)
    },
    onError: options?.onError,
  })
}

export const useApproveProviderInstructionMutation = (options?: {
  onSuccess?: (data: ProviderInstruction | undefined) => void
  onError?: (err: unknown) => void
}) => {
  return useMutation({
    mutationKey: patientKeys.approveInstruction(),
    mutationFn: async (vars: { patientId: number; instructionId: number }) => {
      const res = await api
        // @ts-expect-error schema declares both path params as `id`; interpolate directly
        .POST(`/patients/${vars.patientId}/instructions/${vars.instructionId}/approve/`, {})
      return res.data as ProviderInstruction | undefined
    },
    onSuccess: (data, vars) => {
      invalidateInstructions(vars.patientId)
      options?.onSuccess?.(data)
    },
    onError: options?.onError,
  })
}

export type ProviderInstructionReminder =
  components["schemas"]["ProviderInstructionReminder"]

/**
 * Reminders (or medication doses) for a SINGLE instruction over a look-back
 * window. Factory rather than a hook so a caller can fan several out through
 * `useQueries`. Uses the interpolated-URL escape hatch because the schema names
 * both path params `id`.
 */
export const providerInstructionRemindersQueryOptions = (
  patientId: number,
  instructionId: number,
  days: number,
  enabled = true
) =>
  queryOptions({
    queryKey: patientKeys.instructionReminders(patientId, instructionId, days),
    queryFn: async () => {
      const res = await api
        // @ts-expect-error schema declares both path params as `id`; interpolate directly
        .GET(`/patients/${patientId}/instructions/${instructionId}/reminders/?days=${days}`)
      return (res.data ?? []) as ProviderInstructionReminder[]
    },
    enabled: enabled && !!patientId && !!instructionId,
    staleTime: 60 * 1000, // 1 min
  })

/** AI-structure the clinician's free text before saving. Nothing is persisted;
 * the response pre-fills the form for them to confirm. */
export const useDraftProviderInstructionMutation = (
  mutationOptions?: MutOpts<"post", "/patients/{id}/instructions/draft/">
) => {
  return useMutation({
    mutationKey: patientKeys.draftInstruction(),
    mutationFn: (fetchOpts) =>
      api.POST("/patients/{id}/instructions/draft/", fetchOpts),
    ...mutationOptions,
  })
}

export const useAcknowledgeApneaScreeningMutation = (
  mutationOptions?: MutOpts<"patch", "/patients/{id}/apnea-screenings/{screening_id}/acknowledge/">
) => {
  return useMutation({
    mutationKey: patientKeys.acknowledgeApneaScreening(),
    mutationFn: fetchOpts => {
      return api.PATCH("/patients/{id}/apnea-screenings/{screening_id}/acknowledge/", fetchOpts)
    },
    ...mutationOptions,
    onSuccess: (data, variables, context) => {
      const id = variables?.params?.path.id
        queryClient.invalidateQueries({
          queryKey: patientKeys.apneaScreenings(id),
        })
  
      return mutationOptions?.onSuccess?.(data, variables, context)
    },
  })
}
